import { apiService } from './api';

export interface ModerationQueueResponse {
  success: boolean;
  message: string;
  high_priority: any[];
  medium_priority: any[];
  low_priority: any[];
  total_count: number;
  summary: any;
}

export interface ReviewModerationRequest {
  action: 'approve' | 'remove' | 'flag' | 'warn_user' | 'suspend_user';
  reason: string;
  notes?: string;
  flag_id?: string;
}

export interface LocationModerationRequest {
  action: 'approve' | 'reject' | 'request_revision';
  reason: string;
  notes?: string;
  modifications?: Record<string, any>;
  flag_id?: string;
}

export interface BulkModerationRequest {
  content_ids: string[];
  action: string;
  reason: string;
  content_type: string;
}

export interface UserActionRequest {
  action: 'warn' | 'suspend' | 'ban';
  reason: string;
  duration_days?: number;
}

class ModerationService {
  async getModerationQueue(
    contentType?: string,
    priority?: string,
    status: string = 'pending',
    limit: number = 50
  ): Promise<ModerationQueueResponse> {
    const params = new URLSearchParams({
      status,
      limit: limit.toString(),
    });
    
    if (contentType) params.append('content_type', contentType);
    if (priority) params.append('priority', priority);
    
    const response: any = await apiService.get(`/moderation/queue?${params}`);
    return response.data;
  }

  async moderateReview(
    reviewId: string,
    request: ReviewModerationRequest
  ): Promise<any> {
    const response: any = await apiService.post(
      `/moderation/reviews/${reviewId}/moderate`,
      request
    );
    return response.data;
  }

  async moderateLocationSubmission(
    submissionId: string,
    request: LocationModerationRequest
  ): Promise<any> {
    const response: any = await apiService.post(
      `/moderation/location-submissions/${submissionId}/moderate`,
      request
    );
    return response.data;
  }

  async bulkModerateContent(request: BulkModerationRequest): Promise<any> {
    const response: any = await apiService.post('/moderation/bulk-moderate', request);
    return response.data;
  }

  async getUserModerationHistory(
    userId: string,
    limit: number = 20
  ): Promise<any> {
    const response: any = await apiService.get(
      `/moderation/users/${userId}/history?limit=${limit}`
    );
    return response.data;
  }

  async getRepeatOffenders(
    days: number = 30,
    minViolations: number = 3
  ): Promise<any> {
    const response: any = await apiService.get(
      `/moderation/repeat-offenders?days=${days}&min_violations=${minViolations}`
    );
    return response.data;
  }

  async takeUserAction(
    userId: string,
    request: UserActionRequest
  ): Promise<any> {
    const response: any = await apiService.post(
      `/moderation/users/${userId}/action`,
      request
    );
    return response.data;
  }

  async getContentQualityAnalytics(days: number = 30): Promise<any> {
    const response: any = await apiService.get(
      `/moderation/analytics/content-quality?days=${days}`
    );
    return response.data.data;
  }

  async getModerationActivityAnalytics(days: number = 30): Promise<any> {
    const response: any = await apiService.get(
      `/moderation/analytics/moderation-activity?days=${days}`
    );
    return response.data.data;
  }

  async flagContent(
    contentId: string,
    contentType: string,
    reason: string,
    priority: string = 'medium'
  ): Promise<any> {
    const response: any = await apiService.post(`/moderation/content/${contentId}/flag`, {
      content_type: contentType,
      reason,
      priority,
    });
    return response.data;
  }

  async getUserBehaviorAnalytics(
    days: number = 30,
    minActivityThreshold: number = 5
  ): Promise<any> {
    const response: any = await apiService.get(
      `/moderation/analytics/user-behavior?days=${days}&min_activity_threshold=${minActivityThreshold}`
    );
    return response.data.data;
  }

  async getLocationUsageAnalytics(days: number = 30): Promise<any> {
    const response: any = await apiService.get(
      `/moderation/analytics/location-usage?days=${days}`
    );
    return response.data.data;
  }

  async detectUserSuspiciousActivity(
    userId: string,
    hoursLookback: number = 24
  ): Promise<any> {
    const response: any = await apiService.get(
      `/moderation/users/${userId}/suspicious-activity?hours_lookback=${hoursLookback}`
    );
    return response.data.data;
  }

  async trackUserBehavior(
    userId: string,
    action: string,
    contentType: string,
    contentId: string,
    metadata?: Record<string, any>
  ): Promise<any> {
    const response: any = await apiService.post(
      `/moderation/users/${userId}/track-behavior`,
      {
        action,
        content_type: contentType,
        content_id: contentId,
        metadata,
      }
    );
    return response.data;
  }
}

export const moderationService = new ModerationService();